import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Table } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { StopCircle } from "lucide-react";
import { api } from "@/lib/api";
import { DetailHeader } from "@/components/layout/detail-header";
import { useToast } from "@/contexts/toast-context";
import { ConfirmationModal } from "@/components/ui/confirmation-modal";

interface RunningTest {
  id: string;
  testId: string;
  testName: string;
  runId: string;
  externalId: string;
  currentStep: string;
  currentStepType: string;
  state: string;
  createdAt: string;
  groupId: string;
}

export function RunningTestsPage() {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [runningTests, setRunningTests] = useState<RunningTest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCancelling, setIsCancelling] = useState<string | null>(null);
  const [showCancelModal, setShowCancelModal] = useState<string | null>(null);

  useEffect(() => {
    loadRunningTests();

    // Set up polling every 5 seconds for real-time updates
    const interval = setInterval(loadRunningTests, 5000);

    return () => clearInterval(interval);
  }, []);

  const loadRunningTests = async () => {
    try {
      if (runningTests.length === 0) {
        setIsLoading(true);
      }
      const response = await api.get<RunningTest[]>("/tests/running");
      setRunningTests(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error("Failed to load running tests:", error);
      showError("Failed to load running tests", "Could not fetch running tests. Please try again.");
      setRunningTests([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelRun = async (runningTest: RunningTest) => {
    try {
      setIsCancelling(runningTest.runId);
      await api.post(`/test/${runningTest.testId}/run-groups/${runningTest.groupId}/cancel`, {});
      showSuccess("Run cancelled successfully", `Test run for "${runningTest.testName}" has been cancelled`);
      setShowCancelModal(null);
      // Reload to update the list
      await loadRunningTests();
    } catch (error) {
      console.error("Failed to cancel run:", error);
      showError("Failed to cancel run", "Could not cancel the test run. Please try again.");
    } finally {
      setIsCancelling(null);
    }
  };

  const handleTestClick = (testId: string) => {
    navigate(`/app/tests/${testId}/runs`);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "Unknown date";
    
    try {
      const date = new Date(dateString);
      if (Number.isNaN(date.getTime())) {
        return "Unknown date";
      }
      return date.toLocaleString();
    } catch (error) {
      return "Unknown date";
    }
  };

  const getStateColor = (state: string) => {
    switch (state) {
      case 'RUNNING':
        return 'text-blue-600';
      case 'INIT':
        return 'text-yellow-600';
      default:
        return 'text-muted-foreground';
    }
  };

  const breadcrumb = [
    { label: "Running Tests" }
  ];

  const columns = [
    {
      key: "testName" as keyof RunningTest,
      header: "Test Name",
      width: "30%",
      render: (runningTest: RunningTest) => (
        <button
          onClick={() => handleTestClick(runningTest.testId)}
          className="text-left hover:text-primary transition-colors cursor-pointer"
        >
          <div className="font-medium text-xs">{runningTest.testName}</div>
        </button>
      )
    },
    {
      key: "externalId" as keyof RunningTest,
      header: "Run ID",
      width: "15%",
      render: (runningTest: RunningTest) => (
        <button
          onClick={() => handleTestClick(runningTest.testId)}
          className="text-left hover:text-primary transition-colors cursor-pointer"
        >
          <div className="text-xs text-muted-foreground">#{runningTest.externalId}</div>
        </button>
      )
    },
    {
      key: "currentStep" as keyof RunningTest,
      header: "Current Step",
      width: "25%",
      render: (runningTest: RunningTest) => (
        <div className="text-xs">
          <div className="font-medium truncate">{runningTest.currentStep}</div>
          <div className="text-muted-foreground text-xs truncate">{runningTest.currentStepType}</div>
        </div>
      )
    },
    {
      key: "state" as keyof RunningTest,
      header: "Status",
      width: "10%",
      render: (runningTest: RunningTest) => (
        <div className={`text-xs font-medium ${getStateColor(runningTest.state)}`}>
          <div className="flex items-center">
            <div className={`w-2 h-2 rounded-full mr-2 ${
              runningTest.state === 'RUNNING' ? 'bg-blue-500 animate-pulse' :
              runningTest.state === 'INIT' ? 'bg-yellow-500' :
              'bg-gray-500'
            }`} />
            {runningTest.state}
          </div>
        </div>
      )
    },
    {
      key: "createdAt" as keyof RunningTest,
      header: "Started",
      width: "15%",
      render: (runningTest: RunningTest) => (
        <div className="text-xs text-muted-foreground">
          {formatDate(runningTest.createdAt)}
        </div>
      )
    },
    {
      key: "actions",
      header: "",
      width: "5%",
      className: "text-right",
      render: (runningTest: RunningTest) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            setShowCancelModal(runningTest.runId);
          }}
          disabled={isCancelling === runningTest.runId}
          className="text-destructive hover:text-destructive hover:bg-destructive/10 h-8 w-8 p-0"
          title="Cancel run"
        >
          <StopCircle className="w-4 h-4" />
        </Button>
      )
    }
  ];

  return (
    <div className="flex flex-col h-full">
      <DetailHeader
        breadcrumb={breadcrumb}
      />
      
      <div className="flex-1 overflow-hidden">
        {isLoading && runningTests.length === 0 ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-sm text-muted-foreground">Loading running tests...</div>
          </div>
        ) : runningTests.length === 0 ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center text-muted-foreground">
              <div className="text-sm">No tests currently running</div>
              <div className="text-xs mt-1">Running tests will appear here automatically</div>
            </div>
          </div>
        ) : (
          <Table
            data={runningTests}
            columns={columns}
            isLoading={false}
            emptyMessage="No running tests found"
            fixedHeader={true}
          />
        )}
      </div>

      {/* Cancel Run Confirmation Modal */}
      <ConfirmationModal
        isOpen={!!showCancelModal}
        onClose={() => setShowCancelModal(null)}
        onConfirm={() => {
          const runningTest = runningTests.find(rt => rt.runId === showCancelModal);
          if (runningTest) {
            handleCancelRun(runningTest);
          }
        }}
        title="Cancel Test Run"
        message="Are you sure you want to cancel this test run? This will stop the current execution and mark the run as cancelled. This action cannot be undone."
        confirmText="Cancel Run"
        type="danger"
        isLoading={isCancelling === showCancelModal}
      />
    </div>
  );
}
